import {
  AutoAwesome as GenerateIcon,
  PostAdd as GeneratedPostIcon,
  GridView as GridViewIcon,
  ViewList as ViewListIcon,
} from '@mui/icons-material';
import YouTubeIcon from '@mui/icons-material/YouTube';
import {
  Box,
  Button,
  Checkbox,
  Box as MuiBox,
  Tab,
  Tabs,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
} from '@mui/material';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import ConfirmationDialog from 'src/components/ConfirmationDialog';
import HttpServices from 'src/services/httpService';
import { userModuleURL } from 'src/services/urlService';
import { CommonHelper } from 'src/utils/commonHelper';
import { useToaster } from 'src/utils/toaster/toasterContext';
import GeneratedPostGridViewPendingApproval from './generated-posts/pending-approval/grid-view/generated-post-pending-approval.component';
import GeneratedPostListViewPendingApproval from './generated-posts/pending-approval/list-view/generated-post-pending-approval.component';
import SemiAutomaticTab from './semi-automatic-tabs.component';

const SemiAutomaticTabsWrapper = ({ imageModels, selectedImageModel, setSelectedImageModel }) => {
  const { t: translate } = useTranslation();
  const [activeTab, setActiveTab] = useState(0);
  const [shouldUpdateElapsedTime, setShouldUpdateElapsedTime] = useState(false);
  const [postViewMode, setPostViewMode] = useState('grid');
  const [approveAllDialogOpen, setApproveAllDialogOpen] = useState(false);
  const [approveAllLoading, setApproveAllLoading] = useState(false);
  const [pendingApprovalPosts, setPendingApprovalPosts] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [sortOrder, setSortOrder] = useState();
  const [sortBy, setSortBy] = useState();
  const { showToast } = useToaster();
  const isCustomDomain = CommonHelper.CustomDomain();
  const isPortalAdmin = CommonHelper.GetBrandInfoFromLocalStorage("is_portal_admin");
  const [isGenerating, setIsGenerating] = useState(false);
  useEffect(() => {
    fetchPendingApprovalPosts('scheduled_time', 'asc');
  }, []);
  const fetchPendingApprovalPosts = (sortByField = '', order = '') => {
    setSortOrder(order);
    setSortBy(sortByField);
    let getProps = {
      url: userModuleURL.getGeneratedPost + `?sort_by=${sortByField}&sort_order=${order}&url_key=jyil-ycmv-lzec-fhaz`,
      successCallback: onFetchSuccess,
      failureCallback: onFetchFailure,
    };
    HttpServices.Get(getProps);
  };
  const onFetchSuccess = (data, message) => {
    message && showToast(message, 's');
    setPendingApprovalPosts(data);
    setIsLoading(false);
  };
  const onFetchFailure = (message) => {
    message && showToast(message, 'e');
    setIsLoading(false);
  };

  const handleReFetch = (sortByField = '', order = '') => {
    setIsLoading(true);
    fetchPendingApprovalPosts(sortByField, order);
  };
  const handleReCallPendingApprovalPost = (sortByField = '', order = '') => {
    setIsLoading(true);
    fetchPendingApprovalPosts(sortByField, order);
  };

  const handleTabChange = (_, newValue) => {
    setActiveTab(newValue);
    if (newValue === 1) {
      // Check if the "Generated Post" tab is clicked
      fetchPendingApprovalPosts('scheduled_time', 'asc');
    }
  };

  const handleApproveAllClick = () => {
    setApproveAllDialogOpen(true);
  };

  const handleApproveAllConfirm = () => {
    setApproveAllLoading(true);
    const props = {
      ai_replace_ellapsed: shouldUpdateElapsedTime,
    };
    const postProps = {
      url: userModuleURL.approveAllGeneratedPost,
      body: props,
      successCallback: (data, message) => {
        message && showToast(message);
        setApproveAllDialogOpen(false);
        setApproveAllLoading(false);
        handleReCallPendingApprovalPost(sortBy, sortOrder); // Recall pending approval posts after success
      },
      failureCallback: (message) => {
        message && showToast(message, 'e');
        setApproveAllDialogOpen(false);
        setApproveAllLoading(false);
      },
    };
    HttpServices.Post(postProps);
  };

  const handleViewModeChange = (_, newMode) => {
    if (newMode) setPostViewMode(newMode);
  };
  const checkGeneratingStatus = async () => {
    const getStatus = () => {
      const getProps = {
        url: userModuleURL.getSemiAutoPostStatus,
        successCallback: onGetStatusSuccess,
        failureCallback: onGetStatusFailure,
      };
      HttpServices.Get(getProps);
    };

    const onGetStatusSuccess = (data) => {
      if (data.is_generating) {
        setIsGenerating(true);
        setTimeout(getStatus, 5000);
      } else {
        setIsGenerating(false);
        fetchPendingApprovalPosts('scheduled_time', 'asc');
      }
    };

    const onGetStatusFailure = (message) => {
      message && showToast(message, 'e');
    };

    getStatus();
  };
  useEffect(() => {
    if (activeTab === 0) {
      checkGeneratingStatus();
    }
  }, [activeTab]);

  return (
    <Box sx={{ width: '100%', height: '100%' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
        <Tabs value={activeTab} onChange={handleTabChange}>
          <Tab
            label={
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <GenerateIcon sx={{ mr: 1 }} />
                {translate('auto_post.generate', 'Generate')}
              </Box>
            }
          />
          <Tab
            label={
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <GeneratedPostIcon sx={{ mr: 1 }} />
                {translate('auto_post.generated_post', 'Generated Post')}
              </Box>
            }
          />
          {(!isCustomDomain || isPortalAdmin) && (
            <Button
              variant="outlined"
              startIcon={<YouTubeIcon />}
              onClick={() => window.open('https://www.youtube.com/watch?v=DRyITzYMa1U', '_blank')}
              color="primary"
            >
              {translate('common.tutorial')}
            </Button>
          )}
        </Tabs>
        {activeTab === 1 && (
          <MuiBox sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Button variant="contained" color="primary" onClick={handleApproveAllClick}>
              {translate('auto_post.approve_all', 'Approve All')}
            </Button>
            <MuiBox sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Checkbox
                id="update-elapsed-time-checkbox"
                checked={shouldUpdateElapsedTime}
                onChange={(e) => setShouldUpdateElapsedTime(e.target.checked)}
                sx={{ p: 0.5, mr: 1 }}
              />
              <label htmlFor="update-elapsed-time-checkbox" style={{ fontSize: 15, cursor: 'pointer' }}>
                {translate('auto_post.ai_adjust_time_for_elapsed_posts', 'AI Adjust time for elapsed posts')}
              </label>
            </MuiBox>
            <ToggleButtonGroup
              value={postViewMode}
              exclusive
              onChange={handleViewModeChange}
              size="small"
              sx={{ height: '36px' }}
            >
              <ToggleButton value="grid" aria-label="grid view">
                <Tooltip title={translate('auto_post.grid_view', 'Grid View')}>
                  <GridViewIcon fontSize="small" />
                </Tooltip>
              </ToggleButton>
              <ToggleButton value="list" aria-label="list view">
                <Tooltip title={translate('auto_post.list_view', 'List View')}>
                  <ViewListIcon fontSize="small" />
                </Tooltip>
              </ToggleButton>
            </ToggleButtonGroup>
          </MuiBox>
        )}
      </Box>
      {activeTab === 0 && (
        <SemiAutomaticTab
          isGenerating={isGenerating}
          checkGeneratingStatus={checkGeneratingStatus}
          imageModels={imageModels}
          selectedImageModel={selectedImageModel}
          setSelectedImageModel={setSelectedImageModel}
          handleHelperTextClick={() => handleTabChange(null, 1)}
          pendingApprovalPosts={pendingApprovalPosts}
        />
      )}
      {activeTab === 1 && (
        <>
          {postViewMode === 'grid' ? (
            <GeneratedPostGridViewPendingApproval
              pendingApprovalPosts={pendingApprovalPosts}
              handleReCallPendingApprovalPost={handleReCallPendingApprovalPost}
              sortBy={sortBy}
              sortOrder={sortOrder}
            />
          ) : (
            <GeneratedPostListViewPendingApproval
              pendingApprovalPosts={pendingApprovalPosts}
              handleReCallPendingApprovalPost={handleReCallPendingApprovalPost}
              sortBy={sortBy}
              sortOrder={sortOrder}
            />
          )}
          <ConfirmationDialog
            open={approveAllDialogOpen}
            onClose={() => setApproveAllDialogOpen(false)}
            onConfirm={handleApproveAllConfirm}
            title={translate('auto_post.confirm_approve_all', 'Confirm Approve All')}
            message={translate('auto_post.confirm_approve_all_message', 'Are you sure you want to approve all posts?')}
            loading={approveAllLoading}
          />
        </>
      )}
    </Box>
  );
};

export default SemiAutomaticTabsWrapper;
